/**
 * 日帰り N 日に分ける
 *
 * 候補日を並べて 1 回で最適化し、全部回れる最少の日数だけ使う。今のプランが 1 日目になり、
 * 2 日目以降は同じ条件の新しいプランとして作られる。
 */

import { useMemo, useState } from 'react'

import type { SplitPlanDaysParams } from '@/api/types/route-plan'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSubmitLock } from '@/hooks/useSubmitLock'

const MAX_DAYS = 14
/** 曜日で絞ると候補がなかなか埋まらないので、探す範囲には上限を設ける */
const SEARCH_RANGE_DAYS = 120
const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']
const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6]
const DEFAULT_DAY_COUNT = 7

const SEARCH_MODES: { isDeep: boolean; label: string }[] = [
  { isDeep: false, label: 'すぐ' },
  { isDeep: true, label: 'じっくり' },
]

const pad = (value: number): string => String(value).padStart(2, '0')
const toKey = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/** 開始日から、選んだ曜日に当たる日を count 件集める */
const candidateDates = (start: string, count: number, weekdays: number[]): Date[] => {
  const [year, month, day] = start.split('-').map(Number)
  if (!year || !month || !day || weekdays.length === 0) return []
  const dates: Date[] = []
  for (let offset = 0; offset < SEARCH_RANGE_DAYS && dates.length < count; offset++) {
    const d = new Date(year, month - 1, day + offset)
    if (weekdays.includes(d.getDay())) dates.push(d)
  }
  return dates
}

const clampInt = (value: string, min: number, max: number): number => {
  const parsed = Math.floor(Number(value || 0))
  if (!Number.isFinite(parsed)) return min
  return Math.min(Math.max(parsed, min), max)
}

/** 時間・分の両方が空か 0 なら未指定 */
const toTotalMinutes = (hours: number, minutes: number): number | null => {
  const total = hours * 60 + minutes
  return total > 0 ? total : null
}

const toDigits = (value: number): string => (value > 0 ? String(value) : '')

type Props = {
  initialDate: string
  initialMaxMinutes: number | null
  spotCount: number
  /** 「自宅から 9:00 に出て戻る」のような 1 日の条件の説明 */
  dayConditionLabel: string
  /** じっくりなら裏で時間をかけて解かせる */
  onSubmit: (params: SplitPlanDaysParams, isDeep: boolean) => Promise<void>
  /** じっくり最適化が走っている。終わるまで次を投げさせない */
  isDeepRunning?: boolean
}

export const PlanSplitDaysPanel = ({
  initialDate,
  initialMaxMinutes,
  spotCount,
  dayConditionLabel,
  onSubmit,
  isDeepRunning = false,
}: Props) => {
  const [startDate, setStartDate] = useState(initialDate)
  const [dayCount, setDayCount] = useState(DEFAULT_DAY_COUNT)
  const [weekdays, setWeekdays] = useState<number[]>(ALL_WEEKDAYS)
  const [maxMinutes, setMaxMinutes] = useState<number | null>(initialMaxMinutes)
  const [isDeep, setIsDeep] = useState(false)
  const { isSubmitting, run } = useSubmitLock()

  const dates = useMemo(() => candidateDates(startDate, dayCount, weekdays), [startDate, dayCount, weekdays])
  const maxHours = Math.floor((maxMinutes ?? 0) / 60)
  const maxRest = (maxMinutes ?? 0) % 60

  const toggleWeekday = (weekday: number) =>
    setWeekdays((prev) =>
      prev.includes(weekday) ? prev.filter((w) => w !== weekday) : [...prev, weekday].sort((a, b) => a - b),
    )

  const handleSubmit = () => run(() => onSubmit({ dates: dates.map(toKey), max_duration_minutes: maxMinutes }, isDeep))

  return (
    <div className="space-y-4 px-4 pt-4 pb-6">
      <p className="text-sm leading-relaxed text-muted-foreground">
        {dayConditionLabel}日帰りを候補日の数だけ並べ、全部回れる最少の日数でどの日に何を回すかを決めます。
        定休日も考慮されます。
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="split-start">最初の候補日</Label>
          <Input id="split-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="split-count">使ってよい日数（上限）</Label>
          <Input
            id="split-count"
            type="number"
            min={1}
            max={MAX_DAYS}
            value={dayCount}
            onChange={(e) => setDayCount(clampInt(e.target.value, 1, MAX_DAYS))}
          />
        </div>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        この中から全部回れる最少の日数だけを、早い日から使います。最短で回りたいときも多めにしておけば十分です
      </p>

      <div className="space-y-1.5">
        <Label>行ける曜日</Label>
        <div className="grid grid-cols-7 gap-1">
          {ALL_WEEKDAYS.map((weekday) => (
            <Button
              key={weekday}
              type="button"
              size="sm"
              variant={weekdays.includes(weekday) ? 'default' : 'outline'}
              className="px-0!"
              onClick={() => toggleWeekday(weekday)}
            >
              {WEEKDAY_LABELS.at(weekday)}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {dates.map((d) => (
            <span key={toKey(d)} className="rounded-full border bg-muted/40 px-2 py-0.5 text-xs tabular-nums">
              {d.getMonth() + 1}/{d.getDate()}({WEEKDAY_LABELS.at(d.getDay())})
            </span>
          ))}
          {dates.length === 0 && <span className="text-xs text-destructive">候補日がありません</span>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="split-max-hours">1 日にかけられる時間（任意）</Label>
        <div className="flex items-center gap-1.5">
          <Input
            id="split-max-hours"
            type="number"
            min={0}
            max={24}
            className="min-w-0 flex-1"
            value={toDigits(maxHours)}
            placeholder="—"
            onChange={(e) => setMaxMinutes(toTotalMinutes(clampInt(e.target.value, 0, 24), maxRest))}
          />
          <span className="shrink-0 text-sm text-muted-foreground">時間</span>
          <Input
            id="split-max-minutes"
            type="number"
            min={0}
            max={59}
            className="min-w-0 flex-1"
            value={toDigits(maxRest)}
            placeholder="—"
            onChange={(e) => setMaxMinutes(toTotalMinutes(maxHours, clampInt(e.target.value, 0, 59)))}
          />
          <span className="shrink-0 text-sm text-muted-foreground">分</span>
        </div>
        <p className="text-xs text-muted-foreground">
          出発から戻るまでの上限です。空欄ならその日の 24 時までに戻れる範囲で詰めて組みます
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>考える時間</Label>
        <div className="grid grid-cols-2 gap-2">
          {SEARCH_MODES.map((mode) => (
            <Button
              key={mode.label}
              type="button"
              size="sm"
              variant={isDeep === mode.isDeep ? 'default' : 'outline'}
              onClick={() => setIsDeep(mode.isDeep)}
            >
              {mode.label}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {isDeep
            ? '裏で 15 分ほどかけて、より少ない日数や短い移動を探します。画面を閉じても続き、終わったら通知します。枠の消費は「すぐ」と同じです'
            : '数十秒で結果を返します。日数が思ったより多いときは「じっくり」を試してください'}
        </p>
      </div>

      <p className="text-xs text-muted-foreground">
        このプランが 1 日目になり、2 日目以降は新しいプランとして作られます。どの日にも入らなかったスポットは 1
        日目に「回りきれなかった」として残ります。Google の枠はスポット数ぶん消費します（{spotCount} 件）
      </p>

      <Button
        className="w-full"
        disabled={dates.length === 0 || (maxMinutes !== null && maxMinutes < 30) || isSubmitting || isDeepRunning}
        onClick={() => void handleSubmit()}
      >
        {isDeepRunning
          ? 'じっくり最適化の途中です'
          : isSubmitting
            ? '組んでいます…'
            : isDeep
              ? `${dates.length} 日の候補からじっくり組む`
              : `${dates.length} 日の候補から組む`}
      </Button>
    </div>
  )
}
